import { formatForDisplay } from "@tanstack/react-hotkeys";
import { File, Folder, Pencil, Scissors, Trash2 } from "lucide-react";
import { type DragEvent, useState } from "react";

import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuShortcut,
	ContextMenuTrigger,
} from "@lawline/ui/components/context-menu";
import { cn } from "@lawline/ui/core/cn";

import { NameInput } from "./name-input";
import type { FileBrowserItem as Item } from "./file-browser.types";

const CUT_HINT = formatForDisplay("Mod+X");

export function FileBrowserItem({
	item,
	renaming,
	onOpen,
	onRename,
	onDelete,
	onStartRename,
	onCancelRename,
	onMoveInto,
	onReorderBefore,
	onCut,
	cut,
	selected,
	onSelect,
	dragging,
	onDragStart,
	onDragEnd,
}: {
	item: Item;
	renaming: boolean;
	onOpen: () => void;
	onRename: (name: string) => void;
	onDelete: () => void;
	onStartRename: () => void;
	onCancelRename: () => void;
	onMoveInto: (sourceId: string) => void;
	onReorderBefore: (sourceId: string) => void;
	onCut: () => void;
	cut: boolean;
	selected: boolean;
	onSelect: () => void;
	dragging: boolean;
	onDragStart: () => void;
	onDragEnd: () => void;
}) {
	const Icon = item.type === "folder" ? Folder : File;
	const isFolder = item.type === "folder";
	// "inside" = move into folder; "before" = reorder ahead of this item.
	const [drop, setDrop] = useState<"inside" | "before" | null>(null);

	// Left edge of any item = insert before it (the only way to reorder ahead of
	// a leading folder); the rest of a folder = move into it.
	const zoneFor = (e: DragEvent<HTMLElement>) => {
		if (!isFolder) return "before" as const;
		const rect = e.currentTarget.getBoundingClientRect();
		return e.clientX - rect.left < rect.width * 0.35 ? "before" : "inside";
	};

	return (
		<ContextMenu>
			<ContextMenuTrigger
				draggable={!renaming}
				onClick={onSelect}
				onDragStart={(e) => {
					e.dataTransfer.setData("text/plain", item.id);
					e.dataTransfer.effectAllowed = "move";
					onDragStart();
				}}
				onDragEnd={() => {
					setDrop(null);
					onDragEnd();
				}}
				onDragOver={(e) => {
					// Always claim the drop so it never falls through to the canvas
					// (which would move the item to the end), but show no indicator
					// when hovering the dragged item over itself.
					e.preventDefault();
					e.stopPropagation();
					e.dataTransfer.dropEffect = "move";
					if (!dragging) setDrop(zoneFor(e));
				}}
				onDragLeave={(e) => {
					// Ignore a leave into a child of this row, such as the icon or the
					// label, to stop flicker.
					if (!e.currentTarget.contains(e.relatedTarget as Node | null))
						setDrop(null);
				}}
				onDrop={(e) => {
					e.preventDefault();
					e.stopPropagation();
					setDrop(null);
					const sourceId = e.dataTransfer.getData("text/plain");
					if (!sourceId || sourceId === item.id) return;
					if (zoneFor(e) === "inside") onMoveInto(sourceId);
					else onReorderBefore(sourceId);
				}}
				onDoubleClick={() => isFolder && onOpen()}
				onContextMenu={(e) => e.stopPropagation()}
				className={cn(
					"flex w-24 cursor-default flex-col items-center gap-1 rounded-md p-2 text-center outline-none select-none",
					"hover:bg-accent",
					selected && "bg-accent",
					drop === "inside" && "bg-accent ring-primary ring-2",
					cut && "opacity-50",
				)}
			>
				<Icon className="text-muted-foreground size-12" strokeWidth={1} />
				{renaming ? (
					<NameInput
						defaultValue={item.name}
						onCommit={onRename}
						onCancel={onCancelRename}
					/>
				) : (
					<span className="line-clamp-2 w-full text-xs break-words">
						{item.name}
					</span>
				)}
				{item.note && (
					<span className="text-muted-foreground line-clamp-2 w-full text-[10px] leading-tight">
						{item.note}
					</span>
				)}
			</ContextMenuTrigger>
			<ContextMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
				<ContextMenuItem onSelect={onStartRename}>
					<Pencil className="size-4" />
					Rename
				</ContextMenuItem>
				<ContextMenuItem onSelect={onCut}>
					<Scissors className="size-4" />
					Cut
					<ContextMenuShortcut>{CUT_HINT}</ContextMenuShortcut>
				</ContextMenuItem>
				<ContextMenuItem variant="destructive" onSelect={onDelete}>
					<Trash2 className="size-4" />
					Delete
				</ContextMenuItem>
			</ContextMenuContent>
		</ContextMenu>
	);
}
